import { useLayoutEffect, useRef, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./CMCalendarApp.css";

const secondsInDay = 60 * 60 * 24;
const firstDate = new Date(2024, 11, 30, 0, 0, 0, 0);
const parentPattern = ["M", "M", "F", "F", "M", "M", "M", "F", "F", "M", "M", "F", "F", "F"];
const specialDatesOverrides = {
    "2025-12-21": "M",
    "2025-12-22": "F",
    "2025-12-23": "F",
    "2025-12-24": "M",
    "2025-12-25": "M",
    "2025-12-26": "F",
    "2025-12-27": "F",
    "2025-12-28": "F",
    "2025-12-29": "M",
    "2025-12-30": "M",
    "2026-12-31": "M",
    "2026-01-01": "F",
    "2026-01-02": "F",
    "2026-01-03": "M",
    "2026-01-04": "M",
    "2026-01-05": "F",
    "2026-01-06": "F",
};

function toLocalISODate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function overrideSpecialDatesPattern(date, deltaDays) {
    const isoDate = toLocalISODate(date);
    if (Object.hasOwn(specialDatesOverrides, isoDate)) {
        return specialDatesOverrides[isoDate];
    }
    return parentPattern[deltaDays % parentPattern.length];
}

const parentNameMatch = { M: "Manuel", F: "Federica" };
const parentColorMatch = { M: "cm-calendar-blue", F: "cm-calendar-pink" };
const parentTextColorMatch = { M: "cm-text-blue", F: "cm-text-pink" };

function getParentInfoForDate(date) {
    const deltaTimeSeconds =
        (date.getTime() - firstDate.getTime()) * 0.001 +
        (firstDate.getTimezoneOffset() - date.getTimezoneOffset()) * 60;
    const deltaDays = Math.floor(deltaTimeSeconds / secondsInDay);
    const parentCode = overrideSpecialDatesPattern(date, deltaDays);

    return {
        code: parentCode,
        name: parentNameMatch[parentCode],
        className: parentTextColorMatch[parentCode],
    };
}

function fetchTileClassName({ date }) {
    const today = new Date();
    const deltaTimeSeconds =
        (date.getTime() - firstDate.getTime()) * 0.001 +
        (firstDate.getTimezoneOffset() - date.getTimezoneOffset()) * 60;
    const deltaDays = Math.floor(deltaTimeSeconds / secondsInDay);
    const parent = overrideSpecialDatesPattern(date, deltaDays);

    const isToday =
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();

    const formatToday = isToday
        ? " font-bold italic text-blue-700 text-lg underline"
        : "";

    return parentColorMatch[parent] + formatToday;
}

function CMCalendarApp() {
    const monthRefs = useRef([]);
    const scrollContainerRef = useRef(null);
    const headerRef = useRef(null);

    const [value, setValue] = useState(new Date());
    const [year, setYear] = useState(value.getFullYear());

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    const todayInfo = getParentInfoForDate(today);
    const tomorrowInfo = getParentInfoForDate(tomorrow);

    const handlePrevYear = () => setYear((prev) => prev - 1);
    const handleNextYear = () => setYear((prev) => prev + 1);
    const handleToday = () => setYear(new Date().getFullYear());
    const handleSelectYear = (e) => setYear(Number(e.target.value));

    // Scroll automatico al mese corretto
    useLayoutEffect(() => {
        let targetMonth = year === today.getFullYear() ? today.getMonth() : 0;
        const el = monthRefs.current[targetMonth];
        const container = scrollContainerRef.current;
        const headerHeight = headerRef.current?.offsetHeight || 0;

        if (el && container) {
            // Scroll dopo il render completo
            setTimeout(() => {
                // top relativo al container
                const offsetTop = el.offsetTop;
                container.scrollTo({
                    top: offsetTop - headerHeight - 8, // margine extra
                    behavior: "smooth",
                });
            }, 50); // 50ms bastano di solito
        }
    }, [year]);

    return (
        <div className="h-screen flex flex-col">
            <div ref={headerRef} className="sticky top-0 z-20 bg-white">
                <div className="mt-4 flex flex-col items-center">
                    {/* Titolo */}
                    <div className="flex flex-col items-center mb-2 text-center">
                        <div className="text-2xl font-semibold text-slate-800">
                            Chi lo va a prendere all'uscita dalla scuola?
                        </div>
                    </div>

                    {/* Oggi e domani */}
                    <div className="mb-6 text-center space-y-1">
                        <div className="text-xl">
                            <span className="font-semibold">Oggi:</span>{" "}
                            <span className={`font-bold ${todayInfo.className}`}>
                                {todayInfo.name}
                            </span>
                        </div>
                        <div className="text-xl">
                            <span className="font-semibold">Domani:</span>{" "}
                            <span className={`font-bold ${tomorrowInfo.className}`}>
                                {tomorrowInfo.name}
                            </span>
                        </div>
                    </div>

                    {/* Pulsanti anno */}
                    <div className="text-xl font-bold text-blue-700 mb-2">{year}</div>
                    <div className="flex gap-4 mb-4">
                        <button
                            onClick={handlePrevYear}
                            className="px-3 py-1.5 bg-blue-100 border border-blue-400 rounded-lg hover:bg-blue-200 transition-all"
                        >
                            ⬅️
                        </button>
                        <button
                            onClick={handleNextYear}
                            className="px-3 py-1.5 bg-blue-100 border border-blue-400 rounded-lg hover:bg-blue-200 transition-all"
                        >
                            ➡️
                        </button>
                    </div>

                    {/* Pulsante oggi + selettore anno */}
                    <div className="flex flex-wrap gap-3 mb-10 justify-center">
                        <button
                            onClick={handleToday}
                            className="px-4 py-1.5 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all"
                        >
                            Vai all’anno corrente
                        </button>

                        <select
                            value={year}
                            onChange={handleSelectYear}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-gray-700 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            {years.map((y) => (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Calendari mensili */}
            <div
                ref={scrollContainerRef}
                className="flex-1 overflow-y-auto px-6 ios-scroll-fix pb-24"
            >
                <div className="grid gap-6 pb-6 [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))]">
                    {Array.from({ length: 12 }, (_, month) => (
                        <div
                            key={month}
                            ref={(el) => (monthRefs.current[month] = el)}
                            className="shadow-sm hover:shadow-md transition-all rounded-xl border border-gray-200 bg-white p-3"
                        >
                            <Calendar
                                onChange={setValue}
                                value={value}
                                tileClassName={fetchTileClassName}
                                activeStartDate={new Date(year, month, 1)}
                                showNeighboringMonth={false}
                                showFixedNumberOfWeeks={false}
                                nextLabel={null}
                                prevLabel={null}
                                next2Label={null}
                                prev2Label={null}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default CMCalendarApp;
